// combatpro/dice/DiceFaces.js
//
// Combat PRO - DiceFaces (parity restoration of the animated polyhedra dice).
//
// The modular Dice Roller migrated the visual dice as a deferred stub: the
// #vi-dice-faces area was left empty and renderVisualDice / makeDieSVG were never
// ported, so a roll produced only a "Total: N" line. This restores the
// established animated dice faithfully: the same SVG polyhedra (d4 triangle,
// d6 square, d8 diamond, d10 / d12 kite, d20 faceted disc, d100 octagon), the
// same per-die colours, and the same physics roll-in (burst, bounce, micro
// jitter, slide to slot, impact snap, late value reveal).
//
// The animation is imperative by nature (it drove the DOM directly in the
// established build); it is reproduced here inside a single useEffect against a
// ref, keyed on rollId so every roll re-runs it, with full cleanup of every
// requestAnimationFrame / interval / timeout so nothing leaks across rolls or on
// unmount. No React state, no external libraries, no getElementById. Pure render
// of the dice array it is given: [{ sides, value }].

import React, { useRef, useEffect } from 'react';

var DIE_COLORS = {
  4:   { fill: '#c0392b', stroke: '#922b21', text: '#fff' },
  6:   { fill: '#1a5276', stroke: '#154360', text: '#fff' },
  8:   { fill: '#1e8449', stroke: '#196f3d', text: '#fff' },
  10:  { fill: '#6c3483', stroke: '#512e5f', text: '#fff' },
  12:  { fill: '#d35400', stroke: '#a04000', text: '#fff' },
  20:  { fill: '#b7950b', stroke: '#9a7d0a', text: '#fff' },
  100: { fill: '#717d7e', stroke: '#5f6a6a', text: '#fff' }
};

function makeDieSVG(sides, value, isRolling) {
  var c = DIE_COLORS[sides] || DIE_COLORS[20];
  var shape = '';
  var labelY = 50;

  if (sides === 4) {
    shape = '<polygon points="45,6 84,78 6,78" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<polygon points="45,14 78,74 12,74" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 65;
  } else if (sides === 6) {
    shape = '<rect x="8" y="8" width="74" height="74" rx="8" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<rect x="14" y="14" width="62" height="62" rx="5" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 52;
  } else if (sides === 8) {
    shape = '<polygon points="45,4 84,45 45,86 6,45" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<polygon points="45,12 76,45 45,78 14,45" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 50;
  } else if (sides === 10) {
    shape = '<polygon points="45,4 82,32 70,78 20,78 8,32" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<polygon points="45,12 75,35 64,72 26,72 15,35" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 52;
  } else if (sides === 12) {
    shape = '<polygon points="45,4 84,32 70,78 20,78 6,32" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<polygon points="45,12 77,35 64,72 26,72 13,35" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 52;
  } else if (sides === 20) {
    shape = '<circle cx="45" cy="45" r="40" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<line x1="45" y1="45" x2="45" y2="5" stroke="' + c.stroke + '" stroke-width="1.2" opacity="0.5"/>';
    shape += '<line x1="45" y1="45" x2="83" y2="67" stroke="' + c.stroke + '" stroke-width="1.2" opacity="0.5"/>';
    shape += '<line x1="45" y1="45" x2="65" y2="85" stroke="' + c.stroke + '" stroke-width="1.2" opacity="0.5"/>';
    shape += '<line x1="45" y1="45" x2="25" y2="85" stroke="' + c.stroke + '" stroke-width="1.2" opacity="0.5"/>';
    shape += '<line x1="45" y1="45" x2="7" y2="67" stroke="' + c.stroke + '" stroke-width="1.2" opacity="0.5"/>';
    shape += '<polygon points="45,5 83,67 65,85 25,85 7,67" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.6"/>';
    labelY = 50;
  } else {
    shape = '<polygon points="30,6 60,6 84,30 84,60 60,84 30,84 6,60 6,30" fill="' + c.fill + '" stroke="' + c.stroke + '" stroke-width="3"/>';
    shape += '<polygon points="32,12 58,12 78,32 78,58 58,78 32,78 12,58 12,32" fill="none" stroke="' + c.stroke + '" stroke-width="1.5" opacity="0.4"/>';
    labelY = 52;
  }

  var displayVal = isRolling ? Math.floor(Math.random() * sides) + 1 : value;
  var fontSize = displayVal > 99 ? 16 : displayVal > 9 ? 22 : 26;

  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90" width="90" height="90">' +
    '<filter id="glow-' + sides + '"><feGaussianBlur stdDeviation="2" result="coloredBlur"/>' +
    '<feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>' +
    '<g filter="url(#glow-' + sides + ')" opacity="' + (isRolling ? 0.85 : 1) + '">' + shape +
    '<text x="45" y="' + labelY + '" text-anchor="middle" dominant-baseline="middle" font-family="Georgia, serif" font-size="' + fontSize + '" font-weight="bold" fill="' + c.text + '" style="text-shadow:0 1px 2px rgba(0,0,0,0.5)">' + displayVal + '</text>' +
    '<text x="75" y="18" text-anchor="middle" font-family="Arial" font-size="10" fill="' + c.text + '" opacity="0.6">d' + sides + '</text>' +
    '</g></svg>';
}

function DiceFaces(props) {
  var dice = props.dice;
  var rollId = props.rollId;
  var stageRef = useRef(null);

  useEffect(function () {
    var faces = stageRef.current;
    if (!faces) { return undefined; }

    faces.innerHTML = '';
    if (!dice || dice.length === 0) {
      var prompt = document.createElement('span');
      prompt.className = 'vi-dice-prompt';
      prompt.textContent = 'Select dice below then click Roll Custom';
      faces.appendChild(prompt);
      return undefined;
    }

    var cancelled = false;
    var intervals = [];
    var timeouts = [];
    var rafs = [];

    var stageH = 160;
    var dieSize = 90;
    var ground = stageH - dieSize - 4;
    var count = dice.length;

    var wrappers = dice.map(function (die) {
      var sides = die.sides || 20;
      var finalValue = die.value !== undefined ? die.value : die;
      var wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;width:90px;height:90px;left:0;top:0;will-change:transform;';
      wrapper.innerHTML = makeDieSVG(sides, finalValue, true);
      faces.appendChild(wrapper);
      return { wrapper: wrapper, sides: sides, finalValue: finalValue };
    });

    wrappers.forEach(function (w, i) {
      var wrapper = w.wrapper;
      var sides = w.sides;
      var finalValue = w.finalValue;

      var stagger = i * 180 + Math.floor(Math.random() * 160);
      var totalW = faces.offsetWidth || 800;
      var slotW = Math.min(110, Math.floor(totalW / count));
      var finalX = i * slotW + (slotW - dieSize) / 2;

      var x = -dieSize;
      var y = ground;
      var angle = Math.random() * 120 - 60;
      var phase = 'rolling';
      var vx = 14 + Math.random() * 14;
      var vy = 0;
      var spinRate = 8 + Math.random() * 16;
      var bounceVy = -(26 + Math.random() * 22);
      var bounceDrift = -(1 + Math.random() * 6);
      var jitterFrames = 0;
      var jitterX = 0;
      var rightWall = totalW - dieSize;

      var isFlickering = true;
      var flickerInterval = setInterval(function () {
        if (isFlickering && !cancelled) { wrapper.innerHTML = makeDieSVG(sides, finalValue, true); }
      }, 40);
      intervals.push(flickerInterval);

      function animate() {
        if (cancelled) { return; }
        if (phase === 'rolling') {
          vx *= 0.995;
          x += vx;
          angle += spinRate;
          y = ground;
          if (x >= rightWall) { x = rightWall; phase = 'bouncing'; vx = bounceDrift; vy = bounceVy; }
        } else if (phase === 'bouncing') {
          vy += 2.2; y += vy; x += vx; angle += vx * 1.5;
          if (y >= ground) { y = ground; phase = 'jitter'; vx = 0; vy = 0; jitterFrames = 0; jitterX = x; }
        } else if (phase === 'jitter') {
          jitterFrames++;
          var jAmp = Math.max(0, 4 - jitterFrames * 0.38);
          var jOff = (jitterFrames % 2 === 0 ? 1 : -1) * jAmp;
          x = jitterX + jOff;
          angle += (0 - angle) * 0.25;
          if (jitterFrames >= 10) { phase = 'landing'; x = jitterX; }
        } else if (phase === 'landing') {
          x += (finalX - x) * 0.22;
          angle += (0 - angle) * 0.22;
          if (Math.abs(x - finalX) < 0.5 && Math.abs(angle) < 0.5) {
            x = finalX; angle = 0; phase = 'settled'; isFlickering = false;
            clearInterval(flickerInterval);
            wrapper.style.transition = 'transform 50ms cubic-bezier(.3,1.4,.5,1)';
            wrapper.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(1.28)';
            var t1 = setTimeout(function () {
              if (cancelled) { return; }
              wrapper.style.transition = 'transform 110ms cubic-bezier(.2,.8,.4,1)';
              wrapper.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(0.96)';
            }, 50);
            var t2 = setTimeout(function () {
              if (cancelled) { return; }
              wrapper.innerHTML = makeDieSVG(sides, finalValue, false);
              wrapper.style.transition = 'transform 80ms ease-out';
              wrapper.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(1)';
              wrapper.style.filter = 'drop-shadow(0 0 12px rgba(255,200,50,0.85))';
            }, 160);
            timeouts.push(t1, t2);
            return;
          }
        }
        wrapper.style.transform = 'translate(' + x + 'px, ' + y + 'px) rotate(' + angle + 'deg)';
        rafs.push(requestAnimationFrame(animate));
      }

      var startTimer = setTimeout(function () {
        if (cancelled) { return; }
        wrapper.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
        rafs.push(requestAnimationFrame(animate));
      }, stagger);
      timeouts.push(startTimer);
    });

    return function () {
      cancelled = true;
      rafs.forEach(function (id) { cancelAnimationFrame(id); });
      intervals.forEach(function (id) { clearInterval(id); });
      timeouts.forEach(function (id) { clearTimeout(id); });
    };
  }, [rollId]);

  return <div className="vi-dice-faces-stage" ref={stageRef}></div>;
}

export default DiceFaces;
